<script lang="ts" module>
  import type { CheckoutActions, CheckoutState } from '@athena-lib/Checkout/utils';
  const mapOptional = <T, B>(value: T | undefined | null, fn: (value: T) => B) => {
    if (value === undefined || value === null) {
      return value;
    }
    return fn(value);
  };
  const getCustomStickerAttributes = (
    lineItem: CheckoutState['lines'][number],
    checkoutAtlasTranslations:
      | {
          translateMaterial: (material: string) => string;
          translateLaminate: (laminate: string) => string;
        }
      | undefined,
    locale: LocalizationContext,
  ): {
    size: string;
    material: string;
    laminate: string;
    priceList?: CartLineCustomStickerContent['priceList']['prices'];
  } => {
    if (lineItem.content.type !== 'customSticker') {
      return {
        size: '',
        material: '',
        laminate: '',
      };
    }

    const material = mapOptional(
      lineItem.content.attributes['material'] as null | undefined | string,
      checkoutAtlasTranslations?.translateMaterial ?? ((x: string): string => x),
    );
    const laminate = mapOptional(
      lineItem.content.attributes['laminate'] as null | undefined | string,
      checkoutAtlasTranslations?.translateLaminate ?? ((x: string): string => x),
    );
    const width = lineItem.content.attributes['width_mm'];
    const height = lineItem.content.attributes['height_mm'];
    const size =
      typeof width === 'number' && typeof height === 'number'
        ? formatSize(width, height, locale, true)
        : '';

    return {
      size: size,
      material: material ?? '',
      laminate: laminate ?? '',
      priceList: lineItem.content.priceList.prices,
    };
  };
  export type QuoteCardProps = {
    image: {
      src: string;
      alt: string;
    };
    type: string;
    name: string;
    quantity: number;
    totalPrice: string;
    pricePerUnit: string;
    size?: string;
    material?: string;
    laminate?: string;
    priceList?: CartLineCustomStickerContent['priceList']['prices'];
    customSticker: Promise<CustomStickerWithId | undefined>;
    onQuantityChange?: (quantity: number) => Promise<unknown>;
    onRemove?: () => Promise<unknown>;
    onDuplicate?: () => Promise<unknown>;
    onEdit?: () => Promise<unknown>;
    onPreview?: () => Promise<unknown>;
  };
  export const lineItemToQuoteCard = (
    lineItem: CheckoutState['lines'][number],
    formatPrice: (price: number) => string,
    checkoutActions: CheckoutActions,
    onOpenEditor: (lineItem: CheckoutState['lines'][number]) => void,
    getCustomSticker: (id: string) => Promise<CustomStickerWithId | undefined>,
    checkoutAtlasTranslations:
      | {
          translateMaterial: (material: string) => string;
          translateLaminate: (laminate: string) => string;
        }
      | undefined,
    locale: LocalizationContext,
  ): QuoteCardProps => {
    return {
      onQuantityChange: (quantity) =>
        checkoutActions.updateLineItem(lineItem.id, quantity).then((x) => {
          if (!x.success) {
            throw new Error('Failed to update line item');
          }
        }),
      onRemove: () =>
        checkoutActions.removeLineItem(lineItem.id).then((x) => {
          if (!x.success) {
            throw new Error('Failed to remove line item');
          }
        }),
      onEdit: () => {
        onOpenEditor(lineItem);
        return Promise.resolve(null);
      },
      onDuplicate: () =>
        checkoutActions.duplicateCustomStickerLineItem(lineItem.id).then((x) => {
          if (!x.success) {
            throw new Error('Failed to duplicate custom sticker line item');
          }
        }),
      pricePerUnit: formatPrice(lineItem.price.unit.amountInclTax),
      totalPrice: formatPrice(lineItem.price.total.amountInclTax),
      image: { src: lineItem.content.imageUrl ?? '', alt: '' },
      name: lineItem.content.name,
      ...getCustomStickerAttributes(lineItem, checkoutAtlasTranslations, locale),
      quantity: lineItem.quantity,
      type: lineItem.content.type,
      customSticker:
        lineItem.content.type === 'customSticker'
          ? getCustomSticker(lineItem.content.customStickerId)
          : Promise.resolve(undefined),
    };
  };
</script>

<script lang="ts">
  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import TextField from '@athena-lib/Fields/TextField/TextField.svelte';
  import type { LocalizationContext } from '@athena-lib/i18n';
  import { Visibility } from '@athena-lib/Icons';
  import MutationButton from '@athena-lib/MutationButton/MutationButton.svelte';
  import { formatSize } from '@athena-lib/ProductWizard';
  import type { ProductItem } from '@athena-lib/ProductWizard/product/model/ProductItem';
  import type { Part } from '@athena-lib/SvgPreview/types';
  import { Text } from '@athena-lib/Text';
  import { useMutation } from '@athena-lib/useMutation.svelte';
  import type {
    CartLineCustomStickerContent,
    CustomStickerWithId,
  } from '@stickerapp-org/checkout-sdk';
  import { Image } from '@athena-lib/Image';
  import { LiveLineItemStickerPreview } from '@athena-lib/LivePreview';
  import { usePromise } from '@athena-lib/usePromise.svelte';
  import ComboboxSuggestions from '@athena-lib/Fields/TextField/ComboboxSuggestions.svelte';
  import clsx from 'clsx';

  const {
    image,
    type,
    laminate,
    name,
    quantity: initialQuantity,
    totalPrice,
    pricePerUnit,
    size,
    material,
    priceList = [],
    customSticker,
    onQuantityChange,
    onRemove,
    onDuplicate,
    onEdit,
    onPreview,
  }: QuoteCardProps = $props();

  let quantity = $state(initialQuantity);

  const actions = $derived.by(() => {
    return [
      { action: onEdit, label: translations.edit },
      { action: onDuplicate, label: translations.duplicate },
      { action: onRemove, label: translations.remove },
    ].filter((x): x is { action: typeof onEdit; label: string } => !!x.action);
  });

  const translations = getCommonTranslations();
  const quantityMutation = useMutation((quantity: number) => {
    if (onQuantityChange) {
      return onQuantityChange(quantity);
    }
    return Promise.reject('not defined');
  });
  const awaitedCustomSticker = usePromise(() => customSticker);
  let quantityElement = $state<HTMLInputElement | undefined>();
</script>

<li class="relative flex flex-col gap-1 rounded-lg bg-st-white p-3">
  <div class="flex flex-row justify-between">
    <Text element="span" size="small" bold>{name}</Text>
    <Text element="span" size="small" bold>{totalPrice}</Text>
  </div>

  <div class="flex flex-row items-center justify-between">
    <div class="flex gap-4" class:hidden={actions.length === 0}>
      {#each actions as { action, label }}
        {#if action}
          <MutationButton
            unstyled
            wide={false}
            class="text-xs text-st-blue underline"
            createPromise={action}
            aria-label={label}
          >
            <Text size="xsmall" color="blue" class="underline">{label}</Text>
          </MutationButton>
        {/if}
      {/each}
    </div>
    <Text element="span" size="xsmall" color="medium">{pricePerUnit}/{translations.pcs}</Text>
  </div>

  <div class="flex flex-row gap-4 pt-2">
    <div class="flex flex-1 flex-col justify-between gap-4">
      <div class="flex flex-col">
        <Text element="span" size="xsmall">{size}</Text>
        <Text element="span" size="xsmall" color="medium" class="uppercase">{laminate}</Text>
        <Text element="span" size="xsmall">{material}</Text>
      </div>

      <div class="flex flex-col gap-4">
        {#if onQuantityChange}
          {@const mappedPriceList = priceList.map((x) => ({
            label: x,
            onClick: () => {
              quantity = x.quantity;
              quantityMutation.mutate(Number(x.quantity));
            },
          }))}
          {@const minQuantity = Math.min(...priceList.map((x) => x.quantity))}
          {@const maxUnitPrice = Math.max(...priceList.map((x) => x.unitPrice))}
          {@const hasRebates = priceList.find((x) => x.unitPrice !== maxUnitPrice)}

          <ComboboxSuggestions inputElement={quantityElement} suggestions={mappedPriceList}>
            {#snippet renderSuggestion(label)}
              {@const priceDrop = 1 - label.unitPrice / maxUnitPrice}
              <div
                class={clsx(
                  'flex w-full flex-row p-2 ',
                  label.quantity === quantity ? 'bg-gray-100 font-bold' : '',
                )}
              >
                <div class="flex w-24 flex-row justify-center">
                  {label.quantity}
                  {translations.pcs}
                </div>
                <div class="flex w-24 flex-row justify-center">
                  {label.price.totalFormatted}
                </div>
                {#if hasRebates}
                  <div class="flex w-24 flex-row justify-center">
                    {#if priceDrop > 0}
                      <div class={clsx('arrow-pill')}>
                        -{priceDrop.toLocaleString(undefined, {
                          style: 'percent',
                          minimumFractionDigits: 0,
                        })}
                      </div>
                    {/if}
                  </div>
                {/if}
              </div>
            {/snippet}
            {#snippet children(snippet, onKeyDown)}
              <TextField
                min={minQuantity ?? 1}
                bind:inputElement={quantityElement}
                disabled={quantityMutation.state.type === 'loading'}
                type="number"
                class="w-28"
                onkeydown={onKeyDown}
                name="quantity"
                helperElement={snippet}
                label={translations.quantity}
                value={quantity}
                onchange={(e) => quantityMutation.mutate(Number(e.currentTarget.value))}
                aria-label={translations.quantity}
              />
            {/snippet}
          </ComboboxSuggestions>
        {:else}
          <Text element="span" size="small">{quantity} {translations.pcs}</Text>
        {/if}
      </div>
    </div>
    <div class="flex max-h-40 flex-1 items-center justify-center bg-st-gray-200 p-2">
      <!-- TODO: Remove check for image.src once we have the correct parts on sticker shop item -->
      <!-- TODO: Insert specific conditions on showLivePreview -->
      {#if type === 'customSticker' && image.src === ''}
        {#if awaitedCustomSticker.value}
          <LiveLineItemStickerPreview
            productItem={awaitedCustomSticker.value.productItem as ProductItem}
            parts={awaitedCustomSticker.value.parts as Array<Part>}
          />
        {/if}
      {:else}
        <Image src={image.src} alt={image.alt} class="aspect-auto max-w-32 shrink-0" />
      {/if}
    </div>
  </div>

  <div class="relative">
    <button
      type="button"
      class:hidden={!onPreview}
      class="absolute right-0 top-10 rounded-full bg-st-white p-1 shadow-lg outline-none"
      onclick={() => onPreview?.()}
      aria-label={translations.preview}
    >
      <Visibility sizeClass="size-4" />
    </button>
  </div>
</li>

<style>
  .arrow-pill {
    position: relative;
    display: inline-block;
    background-color: #78b480; /* The green color */
    color: white;
    padding: 4px 4px;
    padding-right: 6px;
    border-radius: 0px;
    border-top-right-radius: 2px;
    border-bottom-right-radius: 2px;
  }

  .arrow-pill:before {
    content: '';
    position: absolute;
    left: -12px;
    top: 50%;
    transform: translateY(-50%);
    border-top: 14px solid transparent;
    border-radius: 0px;
    border-bottom: 14px solid transparent;
    border-right: 12px solid #78b480;
  }
</style>
